// Defines a `Quantity` of measure.
//
// All "base" quantities (i.e. cannot be expressed in terms of other quantities) are direct
// subclasses of `Quantity`. A "derived" quantity can be represented as the algebraic combination of
// multiple base quantities using the meta-Quantity classes `ProductOf` and `RatioOf`.
//
// This API describes physical quantities (e.g. Length, Time, Mass, Angle) as well as logical ones
// (e.g. Cardinality, Repetitions, Fraction).
//
// The distinction between some logical quantities may seem subtle. The choice is made for utility
// in API type safety. For example, `Cardinality` and `Repetitions` are both unitless counts, but
// the former is a count of things while the latter is a count of events, so the are used in
// different contexts.
//
// In contrast, such distinctions are sometimes made at the `Measure` API level. For example,
// `Offset` and `Volume` are both three dimensional measurse of `Length`, but used in different
// contexts. We could instead have separated out the Quantities Length (distance) from Length
/// (occupied space). Suchs choices may be inconsistent from an ontological perspective, but are
// selected to optimize overall ease-of-use, clarity, and utility.
export abstract class Quantity {
  protected constructor(readonly label: string) {}

  toString() {
    return this.label;
  }
}

// TODO: Create a function to create all of these.
export class Length extends Quantity {
  static i = new Length();

  constructor() {
    super('length');
  }

  // TODO: How do we make Quantity types unique without this sort of thing that we might forget?
  #uniqueProp = undefined;
}

// N.B. This is an _interval_ of time, not a timestamp.
export class Time extends Quantity {
  static i = new Time();

  private constructor() {
    super('time');
  }

  #uniqueProp = undefined;
}

export class Mass extends Quantity {
  static i = new Mass();

  private constructor() {
    super('mass');
  }

  #uniqueProp = undefined;
}

export class Angle extends Quantity {
  static i = new Angle();

  private constructor() {
    super('angle');
  }

  #uniqueProp = undefined;
}

export class Cardinality extends Quantity {
  static i = new Cardinality();

  private constructor() {
    super('cardinality');
  }

  #uniqueProp = undefined;
}

export class Repetitions extends Quantity {
  static i = new Repetitions();

  constructor() {
    super('repetitions');
  }

  #uniqueProp = undefined;
}

export class Proportion extends Quantity {
  static i = new Proportion();

  private constructor() {
    super('proportion');
  }

  #uniqueProp = undefined;
}

// N.B. United States Currency
export class Currency extends Quantity {
  static i = new Currency();

  private constructor() {
    super('currency');
  }

  #uniqueProp = undefined;
}

export class RatioOf<N extends Quantity, D extends Quantity> extends Quantity {
  public constructor(label: string) {
    super(label);
  }

  #uniquePropN: N | undefined;
  #uniquePropD: D | undefined;
}

export class ProductOf<Q1 extends Quantity, Q2 extends Quantity> extends Quantity {
  public constructor(label: string) {
    super(label);
  }

  #uniqueProp1: Q1 | undefined;
  #uniqueProp2: Q2 | undefined;
}

export type Velocity = RatioOf<Length, Time>;
export type AngularVelocity = RatioOf<Angle, Time>;
export type Acceleration = RatioOf<Velocity, Time>;
export type Momentum = ProductOf<Mass, Velocity>;
export type Force = ProductOf<Mass, Acceleration>;
export type Impulse = ProductOf<Force, Time>;
export type Frequency = RatioOf<Repetitions, Time>;

// TODO(bpm): Lookup table of Product and Ratio units, rather than on-the-fly creation?
